import styled from '@emotion/styled'
import React, { useState } from 'react'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'

import Box from '@/modules/components/Box'
import Button from '@/modules/components/Button'
import Flex from '@/modules/components/Flex'
import { useOneLink } from '@/modules/components/hooks/useOneLink'
import { useUserAgent } from '@/modules/components/hooks/useUserAgent'
import Icon from '@/modules/components/Icon'
import Link from '@/modules/components/Link'
import Typography from '@/modules/components/Typography'
import { LowerRegions, regionCookieName } from '@/modules/configs/region'
import downloadApp from '@/modules/images/download-app.svg'
import { getRegionPath } from '@/modules/utils'

import { CompanyLink, Defi, Eco, Exchange, Learn, Products } from '../data'
import { useToggleMenu } from '../useToggleMenu'
import { CompanyMenu } from './CompanyMenu'
import { DefiMenu } from './DefiMenu'
import { EcoMenu } from './EcoMenu'
import { ExchangeMenu } from './ExchangeMenu'
import { LearnMenu } from './LearnMenu'
import MobileMenuItem from './MobileMenuItem'
import { ProductMenu } from './ProductMenu'
import { useFreeze } from './useFreeze'

const MenuHeader = styled(Flex)`
  height: 62px;
  justify-content: end;
  align-items: right;
`
const Line = styled(Box)`
  width: 1px;
  height: 16px;
  background: #424242;
`

const MenuFooter = styled(Box)`
  padding: 0 32px;
`

const DownloadApp = styled(Flex)`
  height: 48px;
  padding: 0 20px;
  border: 1.5px solid #0b1426;
  border-radius: 4px;
  align-items: center;
`

type MobileMenuProps = {
  onCloseButtonClick: () => void
  onSubMenuItemClick: () => void
  products: Products[]
  isExchange: boolean
  exchange?: Exchange[]
  isDefi: boolean
  defi: Defi[]
  isEco: boolean
  eco: Eco[]
  company: CompanyLink[]
  isShowNFT: boolean
  isShowPrice: boolean
  languages: string[]
  learn?: Learn[]
  allRegion: LowerRegions[]
}

export const MobileMenu = ({
  onCloseButtonClick,
  onSubMenuItemClick,
  products,
  isExchange,
  exchange,
  isDefi,
  defi,
  isEco,
  eco,
  company,
  isShowNFT,
  isShowPrice,
  languages,
  learn,
  allRegion,
}: MobileMenuProps) => {
  const [expandedIndex, setExpandedIndex] = useState(-1)
  const [isLangOpen, toggleLang] = useToggleMenu()
  const { t, i18n } = useTranslation('navigation')
  const userAgent = useUserAgent()
  const isIos = userAgent === 'ios'
  const { queryString } = useOneLink()

  const [, setCookie] = useCookies([regionCookieName])

  const regionPath = getRegionPath(location.pathname)
  const abbrLocation = regionPath ? regionPath : 'global'
  const getRegionFlagIcon = (region: string) => {
    const flagStr = `${region.toUpperCase()}Flag`
    const RegionIcon = (Icon as any)[flagStr]
    return React.createElement(RegionIcon, {
      fontSize: '24px',
      border: '1.5px solid #000000',
      borderRadius: '50%',
      marginRight: '10px',
    })
  }

  const { language: currentLanguage } = i18n

  // TODO why creating this kind of function :-(
  const handleMenuItemClick = (i: number) => () => {
    if (i === expandedIndex) {
      setExpandedIndex(-1)
      return
    }
    setExpandedIndex(i)
  }
  useFreeze()

  return (
    <Box
      width="100%"
      height="100%"
      bg="#fff"
      overflowY="scroll"
      display={'flex'}
      flexDirection={'column'}
      customCss={{ WebkitOverflowScrolling: 'touch' }}
    >
      <MenuHeader px={[4, 8]} borderBottom={'1px solid #CED0D0'}>
        <Flex alignItems="center">
          <Button
            variant="plain"
            onClick={onCloseButtonClick}
            ml={[2, 5]}
            mr={'3px'}
          >
            <Icon.Close fontSize="20px" color="secondaryDarkNavy" />
          </Button>
        </Flex>
      </MenuHeader>
      <Box flex={1} overflow={'auto'}>
        <Flex display={'flex'} height={'100%'} flexDirection={'column'}>
          <MobileMenuItem
            title={t('products.crypto')}
            isExpanded={expandedIndex === 0}
            onClick={handleMenuItemClick(0)}
            content={
              <ProductMenu
                menuData={products}
                onMenuItemClick={onSubMenuItemClick}
              />
            }
          />
          {isExchange && (
            <MobileMenuItem
              title={t('products.exchange')}
              isExpanded={expandedIndex === 1}
              onClick={handleMenuItemClick(1)}
              content={
                <ExchangeMenu
                  menuData={exchange}
                  onMenuItemClick={onSubMenuItemClick}
                />
              }
            />
          )}
          {isDefi && (
            <MobileMenuItem
              title={t('products.defi')}
              isExpanded={expandedIndex === 5}
              onClick={handleMenuItemClick(5)}
              content={
                <DefiMenu
                  menuData={defi}
                  onMenuItemClick={onSubMenuItemClick}
                />
              }
            />
          )}
          {isEco && (
            <MobileMenuItem
              title={t('products.eco')}
              isExpanded={expandedIndex === 6}
              onClick={handleMenuItemClick(6)}
              content={
                <EcoMenu menuData={eco} onMenuItemClick={onSubMenuItemClick} />
              }
            />
          )}
          <MobileMenuItem
            title={t('navbar.company')}
            isExpanded={expandedIndex === 2}
            onClick={handleMenuItemClick(2)}
            content={
              <CompanyMenu
                menuData={company}
                onMenuItemClick={onSubMenuItemClick}
              />
            }
          />
          {learn && (
            <MobileMenuItem
              title={t('navbar.learn')}
              isExpanded={expandedIndex === 3}
              onClick={handleMenuItemClick(3)}
              content={
                <LearnMenu
                  menuData={learn}
                  onMenuItemClick={onSubMenuItemClick}
                />
              }
            />
          )}
          {isShowPrice && (
            <Box>
              <Link
                href="https://crypto.com/price"
                onClick={onSubMenuItemClick}
              >
                <MobileMenuItem title={t('navbar.price')} />
              </Link>
            </Box>
          )}
          {isShowNFT && (
            <Box mb={[5, 4]}>
              <Link href="https://crypto.com/nft" onClick={onSubMenuItemClick}>
                <MobileMenuItem
                  title={t('navbar.nft')}
                  // badgeTitle={t("navbar.new")}
                />
              </Link>
            </Box>
          )}
        </Flex>
      </Box>
      <MenuFooter borderTop={'1px solid #CED0D0'}>
        <Flex
          height={62}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <Link
            href={
              isIos
                ? `https://crypto.onelink.me/RSfq${queryString}`
                : `https://crypto.onelink.me/veNW${queryString}`
            }
          >
            <DownloadApp>
              <Box as="img" src={downloadApp} height={'20px'} />
              <Typography variant="downloadBtn" color={'#0B1426'} ml={1}>
                {t('navbar.downloadButton') as string}
              </Typography>
            </DownloadApp>
          </Link>
          {/* TODO chat widget */}
        </Flex>
      </MenuFooter>
    </Box>
  )
}
